import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DeviceRepresentation,
  SessionRepresentation,
  deleteSession,
  getDevices,
  useEnvironment,
} from '@keycloak/keycloak-account-ui';
import { ArrowForward, Refresh } from '@mui/icons-material';
import {
  Box,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { ReactComponent as WarningIcon } from '@/icons/warning-icon.svg';

import DevicesRow from './DevicesRow';
import { Button } from './ui/Button';
import { desktopMediaQuery, mobileMediaQuery } from './utils/mediaQuery';
import { px2rem } from './utils/px2rem';

type Session = SessionRepresentation & {
  os: string;
  osVersion: string;
  device: string;
  mobile: boolean;
};

export default function Devices() {
  const { t } = useTranslation('sessions');
  const context = useEnvironment();

  const isDesktop = useMediaQuery(desktopMediaQuery);
  const isMobile = useMediaQuery(mobileMediaQuery);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [logoutAllModalOpen, setLogoutAllModalOpen] = useState(false);

  const remapDevicesToSessions = (devices: DeviceRepresentation[]) => {
    let sessions = [] as Session[];

    devices.forEach((device) => {
      device.sessions.forEach((session) => {
        sessions.push({
          ...device,
          ...session,
        });
      });
    });

    return sessions;
  };

  useEffect(() => {
    getDevices({ context }).then((devices) => {
      setSessions(remapDevicesToSessions(devices));
    });
  }, []);

  return (
    <>
      <Modal
        open={logoutAllModalOpen}
        onClose={() => setLogoutAllModalOpen(false)}
      >
        <Box
          sx={{
            borderRadius: 3,
            background: 'white',
            padding: { xs: 2, sm: 4 },
            margin: 'auto',
            marginTop: 'calc(50vh - 150px)',
            maxWidth: 'calc(100% - 32px)',
            width: { xs: 'inherit', sm: 520 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <Box
              component="span"
              style={{
                backgroundColor: '#FEECD7',
                display: 'flex',
                height: 40,
                width: 40,
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 16,
                flexShrink: 0,
              }}
            >
              <WarningIcon />
            </Box>
            <Typography fontWeight={700} fontSize={px2rem(18)}>
              {t('modal.title')}
            </Typography>
          </Box>
          <Typography mb={6}>{t('modal.subtitle')}</Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 3, sm: 2 },
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setLogoutAllModalOpen(false)}
              sx={{ width: { xs: '100%', sm: 120 }, height: 40, flexShrink: 0 }}
            >
              {t('modal.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={() =>
                deleteSession(context).then(() => context.keycloak.logout())
              }
              sx={{
                paddingLeft: 3,
                paddingRight: 2,
                height: 40,
                width: '100%',
              }}
            >
              {!isMobile ? t('modal.confirm') : t('modal.confirmMobile')}
              <ArrowForward sx={{ width: 18, height: 18, marginLeft: 1 }} />
            </Button>
          </Box>
        </Box>
      </Modal>
      <Typography
        fontWeight={{ xs: 800, sm: 400 }}
        fontSize={{ xs: 24, sm: 36 }}
        variant="h4"
      >
        {t('title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'start', md: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
        }}
        mt={6}
        mb={2.5}
      >
        <Typography
          sx={{ fontSize: px2rem(14), maxWidth: 350 }}
          color="secondary"
        >
          {t('subtitle')}
        </Typography>
        <Button
          variant="outlined"
          sx={{
            flexShrink: 0,
            width: 289,
            height: 40,
          }}
          onClick={() => setLogoutAllModalOpen(true)}
        >
          {t('openModal')}
          <ArrowForward sx={{ width: 18, height: 18, marginLeft: 1 }} />
        </Button>
      </Box>
      <Table
        sx={{
          padding: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <TableHead>
          <TableRow sx={{ height: 72 }}>
            <TableCell>{t('table.name')}</TableCell>
            {isDesktop && (
              <>
                <TableCell>{t('table.lastActive')}</TableCell>
                <TableCell>{t('table.expires')}</TableCell>
                <TableCell align="right">{t('table.actions')}</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map((session) => (
            <DevicesRow
              session={session}
              key={session.id}
              refresh={() => {
                getDevices({ context }).then((devices) => {
                  setSessions(remapDevicesToSessions(devices));
                });
              }}
            />
          ))}
        </TableBody>
      </Table>
      <Box sx={{ width: '100%', textAlign: 'center', marginTop: 2.5 }}>
        <Button
          onClick={() => {
            getDevices({ context }).then((devices) => {
              setSessions(remapDevicesToSessions(devices));
            });
          }}
        >
          <Refresh sx={{ width: 18, height: 18, marginRight: 1 }} />
          {t('refresh')}
        </Button>
      </Box>
    </>
  );
}
